import React from 'react';
import { isUndefined } from 'lodash-es';

import {
  CONFIGURATOR_ALL_STEPS,
  CONFIGURATOR_DEFAULT_STEP,
  CONFIGURATOR_LIVE_STEP,
  CONFIGURATOR_PAYMENT_STEP,
  CONFIGURATOR_STEPS,
  CONFIGURATOR_VISITED_STEPS_MAP,
} from '../constants';
import {
  ConfiguratorContextStep,
  ConfiguratorSessionState,
  ConfiguratorStep,
  ConfiguratorStepClickHandler,
  SetSessionCallback,
} from '../types';

export function useSteps(sessionState: ConfiguratorSessionState = {}, setSession: SetSessionCallback) {
  const step = React.useMemo<ConfiguratorStep>(() => {
    const sessionStep = CONFIGURATOR_ALL_STEPS.find(
      (configuratorStep) => configuratorStep.id === sessionState.lastStep,
    );

    return sessionStep ?? CONFIGURATOR_DEFAULT_STEP;
  }, [sessionState.lastStep]);

  const stepsProgress = React.useMemo(() => {
    return sessionState.stepsVisited || CONFIGURATOR_VISITED_STEPS_MAP;
  }, [sessionState.stepsVisited]);

  const isFirstStep = React.useMemo(() => {
    const currentStepIndex = CONFIGURATOR_STEPS.findIndex((configuratorStep) => configuratorStep.id === step.id);

    return currentStepIndex === 0;
  }, [step.id]);

  const isLastStep = React.useMemo(() => {
    const currentStepIndex = CONFIGURATOR_STEPS.findIndex((configuratorStep) => configuratorStep.id === step.id);

    return currentStepIndex === CONFIGURATOR_STEPS.length - 1;
  }, [step.id]);

  const progress = React.useMemo(() => {
    const stepsValidation = Object.values(stepsProgress);
    const completeStepsCount = stepsValidation.filter((isValid) => isValid).length;
    const offset = 100 / stepsValidation.length / 2; // Half step increment to center the progress on the current step

    return (completeStepsCount / stepsValidation.length) * 100 - offset;
  }, [stepsProgress]);

  const handleStepClick = React.useCallback<ConfiguratorStepClickHandler>(
    (nextStep) => {
      setSession({ lastStep: nextStep.id });
    },
    [setSession],
  );

  const handlePreviousStepClick = React.useCallback(() => {
    const currentIndex = CONFIGURATOR_STEPS.findIndex((configuratorStep) => configuratorStep.id === step.id);
    const previousIndex = currentIndex - 1;

    if (previousIndex > -1) {
      setSession({ lastStep: CONFIGURATOR_STEPS[previousIndex].id });
    }
  }, [step, setSession]);

  const handleNextStepClick = React.useCallback(() => {
    const currentIndex = CONFIGURATOR_STEPS.findIndex((configuratorStep) => configuratorStep.id === step.id);
    const nextIndex = currentIndex + 1;

    if (nextIndex < CONFIGURATOR_STEPS.length) {
      setSession({
        lastStep: CONFIGURATOR_STEPS[nextIndex].id,
        stepsVisited: {
          ...stepsProgress,
          [CONFIGURATOR_STEPS[nextIndex].id]: true,
        },
      });
    }

    if (nextIndex === CONFIGURATOR_STEPS.length && stepsProgress[step.id]) {
      setSession({ lastStep: CONFIGURATOR_PAYMENT_STEP.id });
    }
  }, [step, stepsProgress, setSession]);

  const handleCancelPaymentClick = React.useCallback(() => {
    setSession({ lastStep: CONFIGURATOR_LIVE_STEP.id });
  }, [setSession]);

  const steps = React.useMemo<ConfiguratorContextStep[]>(() => {
    return CONFIGURATOR_STEPS.map((configuratorStep, index) => {
      const nextStep = CONFIGURATOR_STEPS[index + 1];
      // If next step is valid, current step should be displayed as being in progress
      const isValid = !isUndefined(nextStep) ? stepsProgress[nextStep.id] : false;

      return {
        ...configuratorStep,
        active: configuratorStep.id === step.id,
        onClick: () => handleStepClick(configuratorStep),
        isValid,
      };
    });
  }, [step, handleStepClick, stepsProgress]);

  return React.useMemo(
    () => ({
      step,
      steps,
      isFirstStep,
      isLastStep,
      onPreviousStepClick: handlePreviousStepClick,
      onNextStepClick: handleNextStepClick,
      progress,
      onCancelPaymentClick: handleCancelPaymentClick,
    }),
    [
      step,
      steps,
      isFirstStep,
      isLastStep,
      handlePreviousStepClick,
      handleNextStepClick,
      progress,
      handleCancelPaymentClick,
    ],
  );
}
